import React, { useState } from 'react';
import { CompRow as CompRowData, FREQ } from '../data';

/** Controlled edit state for a live component row. */
export interface CompEdit {
    frequencyMonths: number;
    minValue: string;   // free-text; '' means "no minimum" (→ null on save)
    isActive: boolean;
}

interface CompRowProps {
    row: CompRowData;
    last?: boolean;
    /** When provided (with onChange), the row is CONTROLLED and editable. */
    edit?: CompEdit;
    freqOptions?: string[];
    freqToLabel?: (months: number) => string;
    labelToMonths?: (label: string) => number;
    onChange?: (patch: Partial<CompEdit>) => void;
    /** Hide the per-component frequency dropdown (frequency is set at the section level). */
    hideFreq?: boolean;
}

/** A single component row inside a Global Settings accordion section.
 *  Toggling the switch off dims the row (opacity .5) and disables its
 *  controls — mirrors toggleRow() from the mockup. */
const CompRow: React.FC<CompRowProps> = ({ row, last, edit, freqOptions, freqToLabel, labelToMonths, onChange, hideFreq }) => {
    const controlled = !!(edit && onChange);
    // Uncontrolled fallback (static mockup mode) keeps its own local toggle.
    const [localChecked, setLocalChecked] = useState(row.checked !== false);
    const checked = controlled ? edit!.isActive : localChecked;
    const locked = !checked;
    const options = freqOptions ?? row.freqOptions ?? FREQ;
    const selectValue = controlled ? freqToLabel!(edit!.frequencyMonths) : row.freq;
    const minValue = controlled ? edit!.minValue : row.threshold;

    const rowClass =
        'comp-row d-flex flex-wrap align-items-center justify-content-between p-3' +
        (last ? '' : ' border-bottom border-light') +
        (row.sub ? ' bg-light bg-opacity-25' : '');

    return (
        <div className={rowClass} style={{ ...(row.sub ? { paddingLeft: '2.5rem' } : {}), opacity: checked ? 1 : 0.5 }}>
            <div className="d-flex align-items-center gap-3 flex-grow-1 min-w-0">
                <div className="form-check form-switch mb-0">
                    <input
                        className="form-check-input" type="checkbox" role="switch" checked={checked}
                        onChange={(e) => (controlled ? onChange!({ isActive: e.target.checked }) : setLocalChecked(e.target.checked))}
                    />
                </div>
                <div className="min-w-0">
                    <div className={`font-13 font-600 ${row.sub ? 'text-secondary' : 'text-dark'} comp-name-text`}>{row.name}</div>
                    <div className="font-11 text-secondary mt-0.5 text-truncate" style={{ maxWidth: 340 }}>{row.desc}</div>
                </div>
            </div>
            <div className="d-flex align-items-center gap-2 mt-2 mt-sm-0 comp-controls">
                {!hideFreq && (
                    <select
                        className="form-select form-select-sm font-12 py-1 px-2 border" style={{ width: 'auto' }}
                        value={controlled ? selectValue : undefined} defaultValue={controlled ? undefined : row.freq}
                        disabled={locked}
                        onChange={(e) => controlled && onChange!({ frequencyMonths: labelToMonths!(e.target.value) })}
                    >
                        {options.map((o) => <option key={o}>{o}</option>)}
                    </select>
                )}
                <div className="d-flex align-items-center gap-1 font-11 text-secondary comp-threshold">
                    <span>Min:</span>
                    <input
                        className="form-control form-control-sm font-12 py-1 px-2 text-center border" type="text"
                        value={controlled ? minValue : undefined} defaultValue={controlled ? undefined : row.threshold}
                        style={{ width: 70 }} disabled={locked}
                        placeholder="—"
                        onChange={(e) => controlled && onChange!({ minValue: e.target.value })}
                    />
                </div>
                {row.pills.map((p, i) => <span key={i} className={p.className} style={p.style}>{p.label}</span>)}
            </div>
        </div>
    );
};

export default CompRow;
